import { MAP_ID_RE, type MapVariant } from "../engine/maps";
import { MapAccessDeniedError } from "../errors";
import type { AccessResult, Entitlements } from "./types";

export const CALENDAR_START_DATE = "2026-06-04";

export function toUtcDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function tryParseDailyMapId(mapId: string): { date: string; variant: MapVariant } | null {
  const match = MAP_ID_RE.exec(mapId);
  if (!match) {
    return null;
  }
  return { date: match[1], variant: match[2] as MapVariant };
}

function isWellFormedDailyMapId(mapId: string): boolean {
  const parsed = tryParseDailyMapId(mapId);
  if (!parsed) {
    return false;
  }
  return parsed.date >= CALENDAR_START_DATE;
}

export function isReleasedDate(date: string, now = new Date()): boolean {
  return date <= toUtcDateString(now);
}

export function evaluateMapAccess({
  mapId,
  entitlements,
  allowUnreleased = false,
  now = new Date(),
}: {
  mapId: string;
  entitlements: Entitlements;
  allowUnreleased?: boolean;
  now?: Date;
}): AccessResult {
  if (!isWellFormedDailyMapId(mapId)) {
    return { ok: false, reason: "invalid-id" };
  }
  const parsed = tryParseDailyMapId(mapId);
  if (!parsed) {
    return { ok: false, reason: "invalid-id" };
  }
  if (!isReleasedDate(parsed.date, now) && !allowUnreleased) {
    return { ok: false, reason: "not-released" };
  }
  if (parsed.variant === "premium" && entitlements.tier === "free") {
    return { ok: false, reason: "premium-required" };
  }
  const todayStr = toUtcDateString(now);
  if (parsed.date < todayStr && entitlements.tier === "free") {
    return { ok: false, reason: "past-day-requires-premium" };
  }
  return { ok: true };
}

export function assertMapAccess({
  mapId,
  entitlements,
  allowUnreleased = false,
  now = new Date(),
}: {
  mapId: string;
  entitlements: Entitlements;
  allowUnreleased?: boolean;
  now?: Date;
}): void {
  const result = evaluateMapAccess({ mapId, entitlements, allowUnreleased, now });
  if (result.ok) return;

  throw new MapAccessDeniedError(result.reason, mapId);
}

export function evaluateFreshRunAccess({
  entitlements,
  hasWon,
}: {
  entitlements: Entitlements;
  hasWon: boolean;
}): AccessResult {
  if (entitlements.tier === "free" && hasWon) {
    return { ok: false, reason: "already-cleared" };
  }
  return { ok: true };
}

export function assertFreshRunAccess({
  mapId,
  entitlements,
  hasWon,
}: {
  mapId: string;
  entitlements: Entitlements;
  hasWon: boolean;
}): void {
  const result = evaluateFreshRunAccess({ entitlements, hasWon });
  if (result.ok) return;

  throw new MapAccessDeniedError(result.reason, mapId);
}
